import { Injectable } from '@angular/core';
import { PagedList } from '@rma/dashboard/generic/domain/to-be-sorted';
import {
  NotificationBellApiService,
  UserNotificationEventType,
  UserNotificationModel,
} from '@rma/dashboard/notification-bell/private/api-user-notifications';
import { NotificationBellEventService } from '@rma/dashboard/notification-bell/private/util-notification-content';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { interval, map, merge, Observable, startWith, Subject, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationBellFacadeService {
  public unreadNotificationsCount$: Observable<number>;
  public notifications$: Observable<PagedList<UserNotificationModel>>;

  private forceRefresh$ = new Subject<void>();

  public constructor(
    private readonly notificationBellApiService: NotificationBellApiService,
    private readonly notificationBellEventService: NotificationBellEventService,
  ) {
    const refreshTimer$ = interval(60 * 1000).pipe(startWith(0));
    const unreadNotificationsRefresh$ = merge(this.forceRefresh$, refreshTimer$).pipe(rmaShareReplay());

    const unreadNotificationsCount$ = unreadNotificationsRefresh$.pipe(
      switchMap(() =>
        this.notificationBellApiService.getNotifications({
          take: 0,
          onlyNew: true,
        }),
      ),
      rmaShareReplay(),
    );

    this.unreadNotificationsCount$ = unreadNotificationsCount$.pipe(map((x) => x.total));
    this.notifications$ = this.notificationBellApiService.getNotifications();
  }

  public markAllAsRead(): void {
    this.notificationBellApiService
      .markAllAsRead()
      .pipe(take(1))
      .subscribe(() => {
        this.forceRefresh$.next();
      });
  }

  public markAsViewed(): void {
    this.notifications$
      .pipe(
        switchMap((notifications) => this.notificationBellEventService.markAllAs(notifications.results, UserNotificationEventType.View)),
        take(1),
      )
      .subscribe(() => this.forceRefresh$.next());
  }
}
